import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/fr";
import api from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { ChatView } from "@/components/ChatView";
import { MessageSquare } from "lucide-react";

dayjs.extend(relativeTime);
dayjs.locale("fr");

const FILTERS = [
  { value: "", label: "Toutes" },
  { value: "active", label: "Actives" },
  { value: "qualified", label: "Qualifiées" },
  { value: "human_takeover", label: "Humain" },
];

export default function ConversationsPage() {
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("");
  const [loaded, setLoaded] = useState(false);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await api.get("/conversations", { params: filter ? { status: filter } : {} });
      setConversations(res.data.items);
      setLoaded(true);
    } catch {
      setLoaded(true);
    }
  }, [filter]);

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 15000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  return (
    <div className="flex h-full">
      {/* Liste */}
      <div
        className={`${selected ? "hidden md:flex" : "flex"} w-full md:w-96 flex-col border-r border-border bg-background`}
        data-testid="conversations-list"
      >
        <div className="px-5 pt-6 pb-3">
          <h2 className="font-serif text-2xl">Conversations</h2>
          <div className="mt-3 flex gap-1.5 flex-wrap">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                data-testid={`filter-${f.value || "all"}`}
                className={`rounded-full px-3 py-1 text-xs transition-colors duration-200 ${
                  filter === f.value
                    ? "bg-foreground text-background"
                    : "bg-secondary text-muted-foreground hover:text-foreground"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="chat-scroll flex-1 overflow-y-auto">
          {conversations.map((conv) => (
            <button
              key={conv.phone}
              onClick={() => setSelected(conv.phone)}
              data-testid={`conversation-item-${conv.phone}`}
              className={`w-full text-left px-5 py-3.5 border-b border-border/60 transition-colors duration-200 hover:bg-secondary ${
                selected === conv.phone ? "bg-secondary" : ""
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <p className="font-medium text-sm truncate">{conv.profile_name || conv.phone}</p>
                <StatusBadge status={conv.status} testId={`badge-${conv.phone}`} />
              </div>
              <p className="mt-1 text-xs text-muted-foreground truncate">
                {conv.last_message_role === "client" ? "" : "↳ "}
                {conv.last_message_text || "Nouvelle conversation"}
              </p>
              <p className="mt-1 text-[10px] text-muted-foreground/70">
                {conv.last_message_at ? dayjs(conv.last_message_at).fromNow() : ""}
                {conv.message_count ? ` · ${conv.message_count} messages` : ""}
              </p>
            </button>
          ))}
          {loaded && conversations.length === 0 && (
            <div className="px-5 py-12 text-center text-sm text-muted-foreground">
              <MessageSquare className="h-8 w-8 mx-auto mb-3 opacity-40" />
              Aucune conversation pour l'instant.
              <br />
              Les messages WhatsApp entrants apparaîtront ici.
            </div>
          )}
        </div>
      </div>

      {/* Détail */}
      <div className={`${selected ? "flex" : "hidden md:flex"} flex-1 flex-col bg-secondary/30`}>
        {selected ? (
          <ChatView phone={selected} onBack={() => setSelected(null)} onChanged={fetchConversations} />
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground px-6 text-center">
            <MessageSquare className="h-10 w-10 mb-4 opacity-30" />
            <p className="font-serif text-xl text-foreground/70">Sélectionnez une conversation</p>
            <p className="text-sm mt-2 max-w-xs">
              Suivez les échanges de l'agent en temps réel et reprenez la main quand vous le souhaitez.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
